"use client";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, TopBar } from "@/components/ui";
import { FoodImg } from "@/components/FoodImg";
import { Price } from "@/components/Price";
import { useOrder } from "@/hooks/useOrder";
import { useCart } from "@/store/cart";
import { useMenu } from "@/hooks/useMenu";
import { buildCartLinesFromOrder } from "@/lib/reorder";
import { orderApi, paymentApi } from "@/api/services";
import {
  clearPendingPaymentReturn,
  readPendingPaymentReturn,
} from "@/lib/pendingPayment";
import { useBranch } from "@/store/branch";
import { PUSH_ORDER_EVENT } from "@/lib/push";
import { PushOptIn } from "@/components/PushOptIn";
import { OrderStatusEnum, OrderTypeEnum, PaymentStatusEnum } from "@/api/online-ordering-service/model";
import { computeOrderEta, stageFromStatus } from "@/lib/orderEta";
import { isArrivalPromptDue } from "@/lib/promise";
import { driverLegFor, driverLegText } from "@/lib/driverLeg";

const TRACKING_STAGE_IDS = ["received", "preparing", "ready", "delivered"] as const;

const awaitingBanner: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: 14,
  borderRadius: "var(--radius-card)",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
};

const iconCircle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  display: "grid",
  placeItems: "center",
  color: "var(--ink-1)",
  cursor: "pointer",
};

function driverNameFrom(driverInfo: { [k: string]: string } | null | undefined): string | null {
  if (!driverInfo) return null;
  const d = driverInfo as Record<string, string>;
  return d.name ?? d.driverName ?? d.fullName ?? null;
}

function paymentBadge(
  s: PaymentStatusEnum | null | undefined,
  t: (k: string) => string,
): {
  tone: "success" | "warn" | "danger" | "neutral";
  label: string;
} {
  switch (s) {
    case PaymentStatusEnum.Paid:
      return { tone: "success", label: t("v2.order_detail.pay_status.paid") };
    case PaymentStatusEnum.PartiallyPaid:
      return { tone: "warn", label: t("v2.order_detail.pay_status.partial") };
    case PaymentStatusEnum.Refunded:
    case PaymentStatusEnum.PartiallyRefunded:
      return { tone: "neutral", label: t("v2.order_detail.pay_status.refunded") };
    case PaymentStatusEnum.Unpaid:
      return { tone: "danger", label: t("v2.order_detail.pay_status.unpaid") };
    default:
      return { tone: "neutral", label: t("v2.order_detail.pay_status.pending") };
  }
}

function formatStatus(status: string | undefined | null): string {
  if (!status) return "—";
  return status
    .replace(/[_-]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function TrackingScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { orderNum } = useParams<{ orderNum: string }>();
  const { branchSlug, branchConfig, branch } = useBranch();
  const { order, loading, error, refresh } = useOrder(orderNum);
  const { setCart } = useCart();
  const { items: menuItems } = useMenu();
  const [confirmingReturn, setConfirmingReturn] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancel = async () => {
    setShowCancelDialog(false);
    setCancelError(null);
    if (!order || !order.id) return;

    setCancelling(true);
    try {
      await orderApi.cancel(order.id, order.orderType || "delivery");
      navigate("/", { replace: true });
    } catch (err) {
      console.warn("[Tracking] cancel order failed", err);
      setCancelError(t("v2.tracking.cancel_failed", "Failed to cancel order. Please try again."));
    } finally {
      setCancelling(false);
    }
  };

  // "Complete payment" for an order stranded at pending_payment (the customer
  // abandoned/cancelled the gateway 3DS). Rather than re-drive the provider SDK
  // from here, restore the items into the cart and send them back through the
  // proven checkout flow. The original unpaid order is auto-cancelled by the
  // order-service reaper, so nothing is double-charged or left dangling.
  const handleCompletePayment = () => {
    if (!order) return;
    const { lines } = buildCartLinesFromOrder(order, menuItems, i18n.language || "en");
    if (!lines.length) {
      // Menu changed and none of the items survive — send them to the menu.
      navigate("/", { replace: true });
      return;
    }
    setCart(lines);
    navigate("/checkout");
  };

  useEffect(() => {
    if (!orderNum || !searchParams.has("paymentReturn")) return;
    const pending = readPendingPaymentReturn(orderNum, branchSlug);
    if (!pending) {
      refresh();
      return;
    }

    let cancelled = false;
    setConfirmingReturn(true);
    paymentApi
      .confirmPayment(orderNum, {
        intentId: pending.intentId || pending.transactionRef,
        transactionRef: pending.transactionRef,
        providerKey: pending.providerKey,
        raw: {
          paymentReturnUrl: window.location.href,
          providerFamily: pending.providerFamily,
        },
      })
      .then(() => {
        if (cancelled) return;
        clearPendingPaymentReturn(orderNum, branchSlug);
        const next = new URLSearchParams(searchParams);
        next.delete("paymentReturn");
        next.delete("provider");
        setSearchParams(next, { replace: true });
        refresh();
      })
      .catch((err) => {
        console.warn("[Tracking] payment return confirmation failed", err);
        refresh();
      })
      .finally(() => {
        if (!cancelled) setConfirmingReturn(false);
      });

    return () => {
      cancelled = true;
    };
  }, [branchSlug, orderNum, refresh, searchParams, setSearchParams]);

  // Instant refresh when a foreground order-status push arrives for this order
  // (the 15s poll is the fallback). Match against the route param, order id, or
  // order number — the push only carries an orderId.
  useEffect(() => {
    function onPush(e: Event) {
      const detail = (e as CustomEvent<{ orderId: string | null }>).detail;
      const pushedId = detail?.orderId;
      if (!pushedId) {
        refresh();
        return;
      }
      if (pushedId === orderNum || pushedId === order?.id || pushedId === order?.orderNumber) {
        refresh();
      }
    }
    window.addEventListener(PUSH_ORDER_EVENT, onPush);
    return () => window.removeEventListener(PUSH_ORDER_EVENT, onPush);
  }, [orderNum, order?.id, order?.orderNumber, refresh]);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => clearInterval(timer);
  }, []);

  const stage = stageFromStatus(order?.status);
  const isPickup = order?.orderType === OrderTypeEnum.Pickup;

  // Promise Time: render the server promise only when this branch shows
  // promises to customers (BranchConfig.visibleToCustomers); otherwise
  // computeOrderEta falls back to legacyEta.
  const promiseVisible =
    branchConfig?.branches?.find((b) => b.branchId === order?.branchId)?.visibleToCustomers === true;
  const [arrivalState, setArrivalState] = useState<"idle" | "sending" | "done" | "dismissed">("idle");
  const showArrivalPrompt =
    (arrivalState === "idle" || arrivalState === "sending") && isArrivalPromptDue(order, promiseVisible, now);
  const confirmArrival = async () => {
    if (!order?.id) return;
    setArrivalState("sending");
    try {
      await orderApi.confirmArrival(order.id);
      setArrivalState("done");
      await refresh();
    } catch {
      setArrivalState("idle");
    }
  };

  const orderStages = useMemo(() => {
    const base = [
      {
        id: "received",
        label: t("v2.tracking.stages.received.label", "Order received"),
        note: t("v2.tracking.stages.received.note", "We've got it"),
      },
      {
        id: "preparing",
        label: t("v2.tracking.stages.preparing.label", "In the kitchen"),
        note: t("v2.tracking.stages.preparing.note", "Your order is being prepared"),
      },
    ];

    if (isPickup) {
      return [
        ...base,
        {
          id: "ready_for_pickup",
          label: t("v2.tracking.stages.ready_for_pickup.label", "Ready for pickup"),
          note: t("v2.tracking.stages.ready_for_pickup.note", "Ready to collect"),
        },
        {
          id: "picked_up",
          label: t("v2.tracking.stages.picked_up.label", "Picked up"),
          note: t("v2.tracking.stages.picked_up.note", "Enjoy"),
        },
      ];
    }

    return [
      ...base,
      {
        id: "ready",
        label: t("v2.tracking.stages.ready.label", "Ready"),
        note: t("v2.tracking.stages.ready.note", "Waiting for handoff"),
      },
      {
        id: "delivered",
        label: t("v2.tracking.stages.delivered.label", "Delivered"),
        note: t("v2.tracking.stages.delivered.note", "Enjoy"),
      },
    ];
  }, [isPickup, t]);

  // Promise Time Phase 2: the driver leg ("Ahmed is 7 min from the store") comes from the
  // polled order (Order.delivery mirror + promise.current). No RTDB subscription and no
  // client-side ETA. Shown only where the branch shows its promise to customers; shadow
  // branches keep the legacy courier line.
  const driverLeg = useMemo(
    () => (promiseVisible ? driverLegFor(order, { locale: i18n.language, showPromise: true }) : null),
    [order, i18n.language, promiseVisible],
  );
  const driverName = driverLeg?.name ?? driverNameFrom(order?.driverInfo);
  const courierLine = driverLeg
    ? driverLegText(driverLeg, t)
    : driverName
    ? t("v2.tracking.driver_on_way", { name: driverName })
    : isPickup
    ? stage === 0
      ? t("v2.tracking.order_received")
      : stage === 1
      ? t("v2.tracking.preparing")
      : stage === 2
      ? t("v2.tracking.ready_for_pickup", "Ready for pickup")
      : t("v2.tracking.picked_up", "Picked up")
    : stage === 0
    ? t("v2.tracking.order_received")
    : stage === 1
    ? t("v2.tracking.preparing")
    : stage === 2
    ? t("v2.tracking.out_for_delivery")
    : t("v2.tracking.delivered");

  const etaInfo = useMemo(() => {
    return computeOrderEta(order, branch, t, {
      now,
      locale: i18n.language,
      promiseVisible,
    });
  }, [order, branch, t, now, i18n.language, promiseVisible]);

  const displayNum = order?.orderNumber
    ? order.orderNumber.split("-").pop() ?? orderNum ?? ""
    : "";
  const isAwaitingPosConfirmation =
    order?.status === OrderStatusEnum.AwaitingPosConfirmation;
  const isDone =
    order?.status === OrderStatusEnum.Completed ||
    order?.status === OrderStatusEnum.Delivered;

  // A cancelled/rejected order must NOT fall through to the in-progress map+ETA
  // view (which reads as "on its way"). It gets its own prominent terminal card.
  const isRejected = order?.status === OrderStatusEnum.Rejected;
  const isCancelled = order?.status === OrderStatusEnum.Cancelled || isRejected;
  // Held online order whose payment never completed — show a clear "finish
  // paying" state instead of the in-progress courier/ETA view (which would
  // falsely read as if the order is being prepared).
  const isAwaitingPayment = order?.status === OrderStatusEnum.PendingPayment;
  const refundedAmount = order?.totalRefunded ?? 0;
  const primaryCard = order?.payments?.find((p) => p.cardDetails)?.cardDetails as
    | { brand?: string; last4?: string; masked?: string }
    | undefined;
  const cardLabel = primaryCard
    ? `${primaryCard.brand ?? t("v2.order_detail.card", "Card")} •• ${primaryCard.last4 ?? primaryCard.masked ?? ""}`.trim()
    : null;

  const scheduledDateTimeLabel = useMemo(() => {
    if (!order?.scheduledTimestamp) return null;
    const parsed = new Date(order.scheduledTimestamp);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString(navigator.language, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [order?.scheduledTimestamp]);


  return (
    <div style={{ paddingBottom: 24 }}>
      <TopBar
        title={
          isDone
            ? isPickup
              ? t("v2.tracking.picked_up", "Picked up")
              : t("v2.tracking.order_done", "Order Done")
            : order?.orderNumber
            ? t("v2.history.order_number", { n: displayNum })
            : t("v2.tracking.number_pending", "Pending")
        }
        onBack={() => navigate("/")}
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {order?.paymentStatus && (
              <Badge
                tone={paymentBadge(order.paymentStatus, t).tone}
                style={{ fontSize: 13, padding: "5px 12px" }}
              >
                {paymentBadge(order.paymentStatus, t).label}
              </Badge>
            )}
            {order?.id && (
              <button
                onClick={() => navigate(`/orders/${order.id}`)}
                style={iconCircle}
                title={t("v2.tracking.view_receipt", "View Receipt")}
              >
                <Icons.receipt size={18} />
              </button>
            )}
          </div>
        }
      />

      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        {!isDone && <PushOptIn />}
        {loading && !order && (
          <Card padding={20} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "var(--ink-2)" }}>{t("v2.tracking.loading")}</div>
          </Card>
        )}
        {confirmingReturn && (
          <Card padding={14} style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Icons.card size={16} style={{ color: "#1d4ed8", marginTop: 1 }} />
              <div style={{ fontSize: 13, color: "#1e40af", fontWeight: 600, lineHeight: 1.45 }}>
                {t("v2.tracking.confirming_payment")}
              </div>
            </div>
          </Card>
        )}
        {error && !order && (
          <Card padding={20} style={{ textAlign: "center" }}>
            <div className="display" style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
              {t("v2.tracking.couldnt_load")}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 12 }}>
              {error.message}
            </div>
            <Button variant="primary" size="sm" onClick={() => refresh()}>
              {t("v2.common.retry")}
            </Button>
          </Card>
        )}

        {order && (
          <>
            {isAwaitingPosConfirmation && (
              <div style={awaitingBanner}>
                <span className="spinner" aria-hidden />
                <div>
                  <strong>
                    {t(
                      "v2.tracking.awaiting_confirmation",
                      "Sit tight — we've sent your order to the restaurant",
                    )}
                  </strong>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--ink-2)" }}>
                    {t(
                      "v2.tracking.awaiting_confirmation_sub",
                      "You'll see your order number as soon as the staff confirms it.",
                    )}
                  </p>
                </div>
              </div>
            )}
            {isDone ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Card
                  padding={24}
                  style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 999,
                      background: "var(--primary-50)",
                      display: "grid",
                      placeItems: "center",
                      color: "var(--primary-600)",
                    }}
                  >
                    <Icons.check size={40} sw={3} />
                  </div>
                  <div className="display" style={{ fontSize: 24, fontWeight: 700 }}>
                    {isPickup
                      ? t("v2.tracking.picked_up", "Picked up")
                      : t("v2.tracking.order_done", "Order Done")}
                  </div>
                  <div
                    style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5, maxWidth: 260 }}
                  >
                    {isPickup
                      ? t(
                          "v2.tracking.order_picked_up_desc",
                          "Your order has been picked up. We hope you enjoy your meal!",
                        )
                      : order.paymentStatus === PaymentStatusEnum.Paid
                      ? t(
                          "v2.tracking.order_done_desc",
                          "Your order has been successfully delivered and paid. We hope you enjoy your meal!",
                        )
                      : t(
                          "v2.tracking.order_delivered_desc",
                          "Your order has been successfully delivered. We hope you enjoy your meal!",
                        )}
                  </div>
                </Card>

                <Button full size="lg" onClick={() => navigate("/")}>
                  {t("v2.success.back_to_menu", "Back to Menu")}
                </Button>
                <Button
                  full
                  variant="outline"
                  size="lg"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  {t("v2.tracking.view_receipt", "View Receipt")}
                </Button>
              </div>
            ) : isCancelled ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Card
                  padding={24}
                  style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                    borderColor: "#fecaca",
                    background: "#fef2f2",
                  }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 999,
                      background: "#fee2e2",
                      display: "grid",
                      placeItems: "center",
                      color: "#dc2626",
                    }}
                  >
                    <Icons.close size={40} sw={3} />
                  </div>
                  <div className="display" style={{ fontSize: 24, fontWeight: 700, color: "#b91c1c" }}>
                    {isRejected
                      ? t("v2.tracking.rejected.title", "Order Rejected")
                      : t("v2.tracking.cancelled.title", "Order Cancelled")}
                  </div>
                  <div style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5, maxWidth: 280 }}>
                    {refundedAmount > 0
                      ? t(
                          "v2.tracking.cancelled.desc_refunded",
                          "This order was cancelled and your payment has been refunded.",
                        )
                      : isRejected
                      ? t(
                          "v2.tracking.rejected.desc",
                          "Sorry — the restaurant couldn't accept this order.",
                        )
                      : t(
                          "v2.tracking.cancelled.desc",
                          "This order has been cancelled.",
                        )}
                  </div>
                  {refundedAmount > 0 && (
                    <div
                      style={{
                        width: "100%",
                        background: "var(--surface-1)",
                        border: "1px solid var(--border)",
                        borderRadius: 12,
                        padding: 14,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ fontSize: 13, color: "var(--ink-2)", fontWeight: 600 }}>
                          {t("v2.order_detail.refunded", "Refunded")}
                        </span>
                        <span className="display" style={{ fontSize: 18, fontWeight: 700, color: "#16a34a" }}>
                          <Price amount={refundedAmount} />
                        </span>
                      </div>
                      {cardLabel && (
                        <div style={{ fontSize: 12, color: "var(--ink-3)", textAlign: "start" }}>
                          {t("v2.tracking.cancelled.refund_to", "Refunded to")} {cardLabel}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: "var(--ink-3)", textAlign: "start" }}>
                        {t(
                          "v2.tracking.cancelled.refund_eta",
                          "Refunds typically take 5–10 business days to appear.",
                        )}
                      </div>
                    </div>
                  )}
                </Card>

                <Button full size="lg" onClick={() => navigate("/")}>
                  {t("v2.success.back_to_menu", "Back to Menu")}
                </Button>
                <Button
                  full
                  variant="outline"
                  size="lg"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  {t("v2.tracking.view_receipt", "View Receipt")}
                </Button>
              </div>
            ) : isAwaitingPayment ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Card
                  padding={24}
                  style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                    borderColor: "#fde68a",
                    background: "#fffbeb",
                  }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 999,
                      background: "#fef3c7",
                      display: "grid",
                      placeItems: "center",
                      color: "#b45309",
                    }}
                  >
                    <Icons.card size={38} />
                  </div>
                  <div className="display" style={{ fontSize: 22, fontWeight: 700, color: "#92400e" }}>
                    {t("v2.tracking.awaiting_payment.title", "Payment not completed")}
                  </div>
                  <div style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5, maxWidth: 290 }}>
                    {t(
                      "v2.tracking.awaiting_payment.desc",
                      "Your order hasn't been sent to the restaurant yet because the payment wasn't completed. Finish paying to place it.",
                    )}
                  </div>
                </Card>

                <Button full size="lg" onClick={handleCompletePayment}>
                  {t("v2.tracking.awaiting_payment.complete", "Complete payment")}
                </Button>
                <Button
                  full
                  variant="outline"
                  size="lg"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={cancelling}
                  style={{ color: "#dc2626", borderColor: "#dc2626", fontWeight: 600 }}
                >
                  {cancelling
                    ? t("v2.common.processing")
                    : t("v2.tracking.cancel_order", "Cancel Order")}
                </Button>
                {cancelError && (
                  <div style={{ color: "var(--danger-600)", fontSize: 13, fontWeight: 600, textAlign: "center", marginTop: 8 }}>
                    {cancelError}
                  </div>
                )}
              </div>
            ) : (
              <>
                <Card padding={0} style={{ overflow: "hidden" }}>
                  <div
                    style={{
                      position: "relative",
                      aspectRatio: "16 / 9",
                      background: "linear-gradient(180deg, #d8eadf 0%, #f4f7f2 100%)",
                    }}
                  >
                    <svg
                      viewBox="0 0 320 180"
                      width="100%"
                      height="100%"
                      style={{ position: "absolute", inset: 0 }}
                    >
                      <defs>
                        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                          <path
                            d="M 20 0 L 0 0 0 20"
                            fill="none"
                            stroke="rgba(0,0,0,0.06)"
                            strokeWidth="1"
                          />
                        </pattern>
                      </defs>
                      <rect width="320" height="180" fill="url(#grid)" />
                      <path
                        d="M 20 150 Q 100 130, 160 110 T 290 50"
                        stroke="var(--primary-600)"
                        strokeWidth="3"
                        fill="none"
                        strokeDasharray="6 6"
                      />
                      <circle cx="20" cy="150" r="8" fill="var(--primary-700)" />
                      <circle cx="290" cy="50" r="8" fill="var(--ink-1)" />
                      <circle
                        cx="160"
                        cy="110"
                        r="14"
                        fill="#fff"
                        stroke="var(--primary-600)"
                        strokeWidth="3"
                      />
                      <text
                        x="160"
                        y="116"
                        fontSize="14"
                        textAnchor="middle"
                        fill="var(--primary-700)"
                        fontWeight="700"
                      >
                        🛵
                      </text>
                    </svg>
                  </div>
                  <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                    <FoodImg
                      tone="rose"
                      square
                      rounded={999}
                      style={{ width: 48, height: 48, aspectRatio: "1/1" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>{courierLine}</div>
                      <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                        {formatStatus(order.status)}
                      </div>
                      {/* E2E L2-11: the rider's live position on the public tracking page. */}
                      {order.delivery?.trackingUrl && !isDone ? (
                        <a
                          href={order.delivery.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="live-tracking-link"
                          style={{ display: "inline-block", marginTop: 6, fontSize: 13, fontWeight: 700, color: "var(--accent)" }}
                        >
                          {t("v2.tracking.live_tracking", "Follow your rider live")}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </Card>

                <Card padding={14}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 14,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, color: "var(--ink-2)", fontWeight: 500 }}>
                        {etaInfo.headline}
                      </div>
                      <div className="display"
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          letterSpacing: -0.5,
                          color: "var(--ink-1)",
                          marginTop: 2,
                        }}
                      >
                        {etaInfo.primaryTime}
                        {etaInfo.unit && (
                          <>
                            {" "}
                            <span style={{ fontSize: 14, color: "var(--ink-2)", fontWeight: 600 }}>
                              {etaInfo.unit}
                            </span>
                          </>
                        )}
                        {scheduledDateTimeLabel && (
                          <span
                            style={{
                              marginInlineStart: 10,
                              fontSize: 12,
                              fontWeight: 600,
                              color: "var(--ink-2)",
                              letterSpacing: 0,
                            }}
                          >
                            {t("v2.checkout.scheduled", "Scheduled")}: {scheduledDateTimeLabel}
                          </span>
                        )}
                      </div>
                      {etaInfo.secondaryText && (
                        <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4, fontWeight: 500 }}>
                          {etaInfo.secondaryText}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {orderStages.map((_, i) => (
                        <div
                          key={i}
                          style={{
                            width: 28,
                            height: 4,
                            borderRadius: 2,
                            background:
                              i <= stage ? "var(--primary-600)" : "var(--surface-2-strong)",
                            transition: "background 300ms",
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {orderStages.map((s, i) => {
                      const done = i < stage;
                      const cur = i === stage;
                      return (
                        <div
                          key={s.id}
                          style={{ display: "flex", gap: 12, paddingTop: i === 0 ? 0 : 8 }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 999,
                                background: done
                                  ? "var(--primary-600)"
                                  : cur
                                  ? "var(--surface-1)"
                                  : "var(--surface-2)",
                                border: cur ? "2px solid var(--primary-600)" : "0",
                                display: "grid",
                                placeItems: "center",
                                boxShadow: cur ? "0 0 0 5px var(--primary-50)" : "none",
                              }}
                            >
                              {done && <Icons.check size={12} stroke="#fff" sw={2.5} />}
                              {cur && (
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 999,
                                    background: "var(--primary-600)",
                                  }}
                                />
                              )}
                            </div>
                            {i < orderStages.length - 1 && (
                              <div
                                style={{
                                  width: 2,
                                  flex: 1,
                                  minHeight: 28,
                                  background: done ? "var(--primary-600)" : "var(--border)",
                                }}
                              />
                            )}
                          </div>
                          <div style={{ flex: 1, paddingBottom: 8 }}>
                            <div className="display"
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: cur || done ? "var(--ink-1)" : "var(--ink-3)",
                              }}
                            >
                              {s.label}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2 }}>
                              {s.note}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {showArrivalPrompt && (
                  <Card padding={14}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                      {t("v2.tracking.arrival_prompt", "Has your order arrived?")}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <Button full onClick={confirmArrival} disabled={arrivalState === "sending"}>
                        {t("v2.tracking.arrival_yes", "Yes, it arrived")}
                      </Button>
                      <Button full variant="ghost" onClick={() => setArrivalState("dismissed")}>
                        {t("v2.tracking.arrival_not_yet", "Not yet")}
                      </Button>
                    </div>
                  </Card>
                )}
                {arrivalState === "done" && (
                  <div style={{ fontSize: 13, color: "var(--ink-2)", fontWeight: 500, textAlign: "center" }}>
                    {t("v2.tracking.arrival_thanks", "Thanks for letting us know.")}
                  </div>
                )}

                {(order.status === OrderStatusEnum.PendingAcceptance ||
                  order.status === OrderStatusEnum.PendingPayment ||
                  order.status === OrderStatusEnum.AwaitingPosConfirmation ||
                  order.status === OrderStatusEnum.Confirmed) && (
                  <div style={{ marginTop: 4 }}>
                    <Button
                      variant="outline"
                      full
                      onClick={() => setShowCancelDialog(true)}
                      disabled={cancelling}
                      style={{
                        color: "#dc2626",
                        borderColor: "#dc2626",
                        fontWeight: 600,
                      }}
                    >
                      {cancelling
                        ? t("v2.common.processing")
                        : t("v2.tracking.cancel_order", "Cancel Order")}
                    </Button>
                    {cancelError && (
                      <div style={{ color: "var(--danger-600)", fontSize: 13, fontWeight: 600, textAlign: "center", marginTop: 8 }}>
                        {cancelError}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {showCancelDialog && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <div
            onClick={() => setShowCancelDialog(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 500,
              background: "var(--surface-1)",
              borderRadius: "24px 24px 0 0",
              boxShadow: "0 -8px 32px rgba(0,0,0,0.15)",
              padding: "16px 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
              animation: "sheet-up 300ms cubic-bezier(0.32, 0.72, 0, 1)",
            }}
          >
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: "var(--surface-2-strong)",
                margin: "2px auto 16px",
              }}
            />
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div className="display" style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)" }}>
                {t("v2.tracking.cancel_confirm_title", "Cancel Order?")}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "var(--ink-2)",
                  marginTop: 6,
                  lineHeight: 1.4,
                }}
              >
                {t("v2.tracking.cancel_confirm_desc", "Are you sure you want to cancel this order? This cannot be undone.")}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Button
                full
                size="lg"
                onClick={handleCancel}
                style={{ background: "#dc2626", borderColor: "#dc2626", color: "#fff" }}
              >
                {t("v2.tracking.cancel_confirm_yes", "Yes, Cancel Order")}
              </Button>
              <Button
                full
                variant="ghost"
                size="lg"
                onClick={() => setShowCancelDialog(false)}
              >
                {t("v2.tracking.cancel_confirm_no", "No, Keep It")}
              </Button>
            </div>
          </div>
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes sheet-up {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
          `}} />
        </div>
      )}
    </div>
  );
}
